import React from 'react';
import {
  isBoolean,
  isArray,
  isObject,
  isString,
  IGNORED_FIELDS,
  normalizeTimeValue,
} from './helpers';
import { isAttachment, isTranslation } from './typeCheckers';
import { getAllAttachmentLabels, getResolvedAttachment } from './labelGetters';
import {
  AttachmentDisplay,
  FieldLabel,
  TranslationDisplay,
  ViewOnlyValue,
} from './displays';
import {
  BooleanDiff,
  TextDiff,
  PrimitiveDiff,
  ArrayDiff,
  ObjectDiff,
} from './diffs';

function FieldDiff({
  fieldName,
  beforeValue,
  afterValue,
  label,
  depth = 0,
  viewOnly = false,
  labels = {},
  attachmentLabels: attachmentLabelsProp,
}) {
  const displayLabel = label || fieldName;
  const attachmentLabels =
    attachmentLabelsProp || getAllAttachmentLabels(labels);

  const renderValue = () => {
    // In view-only mode, just display the value (used for "before" column).
    // The other side is still passed along so untouched items can be left out.
    if (viewOnly) {
      return (
        <ViewOnlyValue
          value={beforeValue}
          compareValue={afterValue}
          attachmentLabels={attachmentLabels}
          labels={labels}
          fieldName={fieldName}
          isBefore={true}
        />
      );
    }

    if (isBoolean(beforeValue) || isBoolean(afterValue)) {
      return <BooleanDiff beforeValue={beforeValue} afterValue={afterValue} />;
    }

    if (isArray(beforeValue) || isArray(afterValue)) {
      return (
        <ArrayDiff
          beforeValue={beforeValue}
          afterValue={afterValue}
          depth={depth}
          viewOnly={viewOnly}
          attachmentLabels={attachmentLabels}
          labels={labels}
          fieldName={fieldName}
        />
      );
    }

    if (isObject(beforeValue) || isObject(afterValue)) {
      // Check for special types that should be displayed with custom components
      if (isAttachment(afterValue)) {
        if (
          isAttachment(beforeValue) &&
          JSON.stringify(beforeValue) === JSON.stringify(afterValue)
        ) {
          const resolvedAttachment = getResolvedAttachment(
            afterValue,
            attachmentLabels,
          );
          return (
            <AttachmentDisplay
              attachment={afterValue}
              resolvedAttachment={resolvedAttachment}
            />
          );
        }
      }
      if (isTranslation(afterValue)) {
        if (
          isTranslation(beforeValue) &&
          JSON.stringify(beforeValue) === JSON.stringify(afterValue)
        ) {
          return <TranslationDisplay translation={afterValue} />;
        }
      }

      return (
        <ObjectDiff
          beforeValue={beforeValue}
          afterValue={afterValue}
          depth={depth}
          attachmentLabels={attachmentLabels}
          labels={labels}
          FieldDiffComponent={FieldDiff}
        />
      );
    }

    if (isString(beforeValue) || isString(afterValue)) {
      const before = beforeValue ?? '';
      const after = afterValue ?? '';
      if (before.length > 100 || after.length > 100) {
        return <TextDiff beforeValue={beforeValue} afterValue={afterValue} />;
      }
      return (
        <PrimitiveDiff
          beforeValue={beforeValue}
          afterValue={afterValue}
          fieldName={fieldName}
          labels={labels}
        />
      );
    }

    return (
      <PrimitiveDiff
        beforeValue={beforeValue}
        afterValue={afterValue}
        fieldName={fieldName}
        labels={labels}
      />
    );
  };

  if (depth > 0) {
    return renderValue();
  }

  return (
    <div className="py-2">
      <FieldLabel>{displayLabel}</FieldLabel>
      {renderValue()}
    </div>
  );
}

export default FieldDiff;
export { IGNORED_FIELDS, normalizeTimeValue };
